// Mesh networking tools and utilities organized by category
import React from 'react';
import { trackExternalLink } from '../utils/analytics';

interface ToolCardProps {
  name: string;
  url: string;
  description: string;
  tags: string[];
  icon: React.ReactNode;
  trackingSource?: string;
}

function ToolCard({ name, url, description, tags, icon, trackingSource }: ToolCardProps) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block group"
      onClick={() => trackingSource && trackExternalLink(url.replace('https://', ''), trackingSource)}
    >
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 transition-all duration-300 hover:border-cyan-400/50 hover:bg-zinc-900/70 hover:shadow-lg hover:shadow-cyan-400/10 h-full">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-400/20 to-cyan-600/20 rounded-lg border border-cyan-400/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              {icon}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-lg font-semibold text-white group-hover:text-cyan-400 transition-colors truncate">
                {name}
              </h4>
              <svg 
                className="w-4 h-4 flex-shrink-0 text-zinc-500 group-hover:text-cyan-400 group-hover:translate-x-0.5 transition-all" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" 
                />
              </svg>
            </div>
            
            <p className="text-sm text-zinc-400 mb-3 leading-relaxed">
              {description}
            </p>

            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <span 
                  key={tag}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-cyan-400/10 text-cyan-400 border border-cyan-400/20"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </a>
  );
}

export function MeshTools() {
  return (
    <section 
      id="mesh-tools" 
      className="relative z-10 mx-auto max-w-7xl px-6 py-16 pointer-events-none"
      style={{ scrollMarginTop: '100px' }}
    >
      <div className="pointer-events-auto">
        <h2 className="text-3xl font-bold mb-4 text-center">
          Mesh <span className="text-cyan-400">Tools & Utilities</span>
        </h2>
        <p className="text-center text-zinc-400 mb-12 max-w-2xl mx-auto">
          Essential tools for monitoring, managing, and analyzing your Meshtastic mesh network.
        </p>

        {/* Monitoring & Analytics Category */}
        <div className="mb-12">
          <h3 className="text-xl font-semibold mb-6 flex items-center gap-3">
            <span className="text-cyan-400">📊</span>
            Monitoring & Analytics
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <ToolCard
              name="Mesh Monitor"
              url="https://meshmonitor.org"
              description="Real-time monitoring and analytics dashboard for Meshtastic networks. Track node status, message flow, and network health metrics."
              tags={['Monitoring', 'Analytics', 'Dashboard']}
              trackingSource="mesh_tools"
              icon={
                <svg 
                  className="w-6 h-6 text-cyan-400" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" 
                  />
                </svg>
              }
            />
          </div>
        </div>

        {/* Configuration & Management Category (placeholder for future) */}
        <div className="mb-12">
          <h3 className="text-xl font-semibold mb-6 flex items-center gap-3">
            <span className="text-cyan-400">⚙️</span>
            Configuration & Management
          </h3>
          <div className="bg-zinc-900/30 border border-zinc-800 border-dashed rounded-xl p-8 text-center">
            <p className="text-zinc-500 text-sm">
              Tools coming soon...
            </p>
          </div>
        </div>

        {/* Development & Testing Category (placeholder for future) */}
        <div>
          <h3 className="text-xl font-semibold mb-6 flex items-center gap-3">
            <span className="text-cyan-400">🛠️</span>
            Development & Testing
          </h3>
          <div className="bg-zinc-900/30 border border-zinc-800 border-dashed rounded-xl p-8 text-center">
            <p className="text-zinc-500 text-sm">
              Tools coming soon...
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

